import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, ArrowRight, CheckCircle, Globe, User, FileText, 
  CreditCard, Camera, Upload, Loader2, Calendar as CalendarIcon, Scan,
  Zap, Shield, Clock, ChevronDown, ChevronUp, Eye, Trash2, Info,
  MapPin, Phone, Mail, Briefcase, Hash, FileCheck, AlertCircle,
  Plus, Minus, ShieldCheck, XCircle, Smartphone, TrendingUp, Save
} from 'lucide-react';
import { format } from 'date-fns';
import { countriesApi, visaTypesApi, applicationsApi, documentsApi, paymentsApi, otpApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';
import { toast } from 'sonner';

const ApplicationWizard = ({ isB2B }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Block unapproved B2B partners
  useEffect(() => {
    if (isB2B && user?.approval_status !== 'approved') {
      toast.error('Your partner account must be approved before submitting applications');
      navigate('/partner/dashboard', { replace: true });
    }
  }, [isB2B, user, navigate]);

  // Edit mode: load existing application data
  const editId = searchParams.get('edit');
  const [editLoading, setEditLoading] = useState(!!editId);

  useEffect(() => {
    if (!editId) return;
    const loadApplication = async () => {
      try {
        const res = await applicationsApi.getById(editId);
        const app = res.data;
        
        setApplicationId(app.application_id);
        setTripDetails({
          country_id: app.country_id || '',
          visa_type_id: app.visa_type_id || '',
          travel_date: app.travel_date ? new Date(app.travel_date) : null,
          return_date: app.return_date ? new Date(app.return_date) : null,
          purpose: app.purpose || '',
          num_travelers: app.num_travelers || 1
        });
        
        if (app.travelers && app.travelers.length > 0) {
          setTravelers(app.travelers.map(t => ({
            first_name: t.first_name || '', last_name: t.last_name || '',
            dob: t.dob || '', gender: t.gender || '', nationality: t.nationality || 'Indian',
            passport_number: t.passport_number || '', passport_expiry: t.passport_expiry || '',
            passport_issue_date: t.passport_issue_date || '', passport_issue_place: t.passport_issue_place || '',
            occupation: t.occupation || '', address: t.address || '', city: t.city || '',
            state: t.state || '', country: t.country || 'India', pincode: t.pincode || '',
            email: t.email || '', phone: t.phone || ''
          })));
        }
        
        if (app.documents && app.documents.length > 0) {
          setDocuments(app.documents);
        }

        if (app.current_step && app.current_step >= 1 && app.current_step <= 4) {
          setCurrentStep(app.current_step);
        }
        if (typeof app.include_appointment === 'boolean') setIncludeAppointment(app.include_appointment);
        if (typeof app.express_processing === 'boolean') setExpressProcessing(app.express_processing);

        toast.success('Application loaded for editing');
      } catch (err) {
        toast.error('Failed to load application');
        console.error('Edit load error:', err);
      } finally {
        setEditLoading(false);
      }
    };
    loadApplication();
  }, [editId]);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  const [expressProcessing, setExpressProcessing] = useState(false);
  const [includeAppointment, setIncludeAppointment] = useState(false);

  // Form data
  const [tripDetails, setTripDetails] = useState({
    country_id: searchParams.get('country') || '',
    visa_type_id: searchParams.get('visa') || '',
    travel_date: null,
    return_date: null,
    purpose: '',
    num_travelers: 1
  });

  const [travelers, setTravelers] = useState([{
    first_name: '', last_name: '', dob: '', gender: '', nationality: 'Indian',
    passport_number: '', passport_expiry: '', passport_issue_date: '',
    passport_issue_place: '', occupation: '', address: '', city: '',
    state: '', country: 'India', pincode: '', email: user?.email || '', phone: ''
  }]);

  const [currentTravelerIndex, setCurrentTravelerIndex] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [scanningPassport, setScanningPassport] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [docCheckResults, setDocCheckResults] = useState({});
  const [checkingDoc, setCheckingDoc] = useState(null);
  const [passportPages, setPassportPages] = useState({});
  const [activePassportStep, setActivePassportStep] = useState('front');
  const [docUploadMode, setDocUploadMode] = useState({});
  const [docUrlInputs, setDocUrlInputs] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [demoOtp, setDemoOtp] = useState('');
  const [returnDateOpen, setReturnDateOpen] = useState(false);

  // Field-level validation errors (red border + shake + auto-focus + inline message)
  const [fieldErrors, setFieldErrors] = useState({});
  const [shakeTrigger, setShakeTrigger] = useState(0);

  const errClass = (field) =>
    fieldErrors[field] ? 'field-error animate-field-shake' : '';

  const FieldError = ({ name }) =>
    fieldErrors[name] ? (
      <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1" data-testid={`error-${name}`}>
        <span className="inline-block w-1 h-1 rounded-full bg-red-600" />
        {fieldErrors[name]}
      </p>
    ) : null;

  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const focusFirstError = (errors) => {
    const order = [
      'country_id', 'visa_type_id', 'travel_date', 'return_date',
      'first_name', 'last_name', 'passport_number', 'passport_expiry',
      'dob', 'gender', 'nationality', 'phone',
    ];
    const firstKey = order.find((k) => errors[k]) || Object.keys(errors)[0];
    if (!firstKey) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-error-key="${firstKey}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = el.querySelector('input, button, [role="combobox"], textarea, select');
      if (focusable) {
        try { focusable.focus({ preventScroll: true }); } catch { /* noop */ }
      }
    }, 50);
  };

  const steps = [
    { number: 1, title: 'Select Plan & Dates', subtitle: 'Choose your visa & travel dates', icon: Globe },
    { number: 2, title: 'Passport Scan', subtitle: 'AI-powered data extraction', icon: Scan },
    { number: 3, title: 'Traveler Details', subtitle: 'Personal information', icon: User },
    { number: 4, title: 'Upload Documents', subtitle: 'Required documents', icon: FileText },
    { number: 5, title: 'Review & Pay', subtitle: 'Confirm & checkout', icon: CreditCard }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const countriesRes = await countriesApi.getAll();
        setCountries(countriesRes.data);
        if (tripDetails.country_id) {
          const visasRes = await countriesApi.getVisaTypes(tripDetails.country_id);
          setVisaTypes(visasRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [tripDetails.country_id]);

  useEffect(() => {
    const num = parseInt(tripDetails.num_travelers) || 1;
    if (num > travelers.length) {
      const newTravelers = [...travelers];
      for (let i = travelers.length; i < num; i++) {
        newTravelers.push({
          first_name: '', last_name: '', dob: '', gender: '', nationality: 'Indian',
          passport_number: '', passport_expiry: '', passport_issue_date: '',
          passport_issue_place: '', occupation: '', address: '', city: '',
          state: '', country: 'India', pincode: '', email: '', phone: ''
        });
      }
      setTravelers(newTravelers);
    } else if (num < travelers.length) {
      setTravelers(travelers.slice(0, num));
      if (currentTravelerIndex >= num) setCurrentTravelerIndex(num - 1);
    }
  }, [tripDetails.num_travelers]);

  const selectedVisa = visaTypes.find(v => v.visa_type_id === tripDetails.visa_type_id);
  const selectedCountry = countries.find(c => c.country_id === tripDetails.country_id);
  const baseFee = (selectedVisa?.fee || 0) * tripDetails.num_travelers;
  const expressFee = expressProcessing ? Math.round(baseFee * 0.5) : 0;
  const appointmentFee = (includeAppointment && selectedVisa?.appointment_enabled) ? (selectedVisa?.appointment_fee || 0) * tripDetails.num_travelers : 0;
  const subtotal = baseFee + expressFee + appointmentFee;
  const partnerDiscount = (isB2B && user?.approval_status === 'approved') ? (user?.discount_percentage || 0) : 0;
  const discountAmount = Math.round(subtotal * (partnerDiscount / 100));
  const partnerCommission = (isB2B && user?.approval_status === 'approved') ? (user?.commission_rate || 0) : 0;
  const commissionAmount = Math.round(subtotal * (partnerCommission / 100));
  const totalFee = subtotal - discountAmount - commissionAmount;

  // Auto-select appointment if visa type has it enabled
  useEffect(() => {
    if (selectedVisa?.appointment_enabled) {
      setIncludeAppointment(true);
    } else {
      setIncludeAppointment(false);
    }
  }, [tripDetails.visa_type_id]);

  const getDeliveryDate = () => {
    const days = expressProcessing ? 2 : 5;
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleTravelerChange = (field, value) => {
    const updated = [...travelers];
    updated[currentTravelerIndex] = { ...updated[currentTravelerIndex], [field]: value };
    setTravelers(updated);
    clearFieldError(field);
  };

  const handlePassportScan = async (file, side = 'front') => {
    setScanningPassport(true);
    
    // Save preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const key = `${currentTravelerIndex}_${side}`;
      setPassportPages(prev => ({ ...prev, [key]: { preview: e.target.result, status: 'scanning', filename: file.name } }));
    };
    reader.readAsDataURL(file);
    
    try {
      const response = await documentsApi.passportScan(file, side);
      const result = response.data;
      const key = `${currentTravelerIndex}_${side}`;
      
      if (result.is_passport && result.data) {
        const data = result.data;
        const updated = [...travelers];
        if (side === 'front') {
          updated[currentTravelerIndex] = {
            ...updated[currentTravelerIndex],
            first_name: data.first_name || updated[currentTravelerIndex].first_name,
            last_name: data.last_name || updated[currentTravelerIndex].last_name,
            dob: data.dob || updated[currentTravelerIndex].dob,
            gender: data.gender || updated[currentTravelerIndex].gender,
            nationality: data.nationality || updated[currentTravelerIndex].nationality,
            passport_number: data.passport_number || updated[currentTravelerIndex].passport_number,
            passport_expiry: data.passport_expiry || updated[currentTravelerIndex].passport_expiry,
            passport_issue_date: data.passport_issue_date || updated[currentTravelerIndex].passport_issue_date
          };
        } else {
          updated[currentTravelerIndex] = {
            ...updated[currentTravelerIndex],
            address: data.address || updated[currentTravelerIndex].address,
            passport_issue_place: data.passport_issue_place || updated[currentTravelerIndex].passport_issue_place
          };
        }
        setTravelers(updated);
        setPassportPages(prev => ({ ...prev, [key]: { ...prev[key], status: 'verified', authenticity: result.confidence || 95 } }));
        toast.success(`Passport ${side} page verified!`);
        if (side === 'front') setActivePassportStep('back');
      } else {
        setPassportPages(prev => ({ ...prev, [key]: { ...prev[key], status: 'failed' } }));
        toast.error('Could not extract passport data. Please try again or enter manually.');
      }
    } catch (error) {
      console.error('Passport scan error:', error);
      const key = `${currentTravelerIndex}_${side}`;
      setPassportPages(prev => ({ ...prev, [key]: { ...prev[key], status: 'failed' } }));
      toast.error('Failed to scan passport. Please try again.');
    } finally {
      setScanningPassport(false);
    }
  };

  const handleFileUpload = async (file, documentType) => {
    if (!applicationId) {
      toast.error('Please complete the previous steps first');
      return;
    }
    
    setCheckingDoc(documentType);
    
    try {
      // Step 1: Run AI auto-check FIRST
      const checkResponse = await documentsApi.autoCheck(file, documentType).catch(() => null);
      const checkKey = `${documentType}_${currentTravelerIndex}`;
      
      if (checkResponse?.data) {
        setDocCheckResults(prev => ({ ...prev, [checkKey]: checkResponse.data }));
        const status = checkResponse.data.overall_status;
        
        // If AI check FAILS → do NOT upload
        if (status === 'fail') {
          toast.error(`${file.name} failed verification. Please fix issues and re-upload.`);
          setCheckingDoc(null);
          return;
        }
      }
      
      // Step 2: Only upload if check passed or had warnings (or check was unavailable)
      const uploadResponse = await documentsApi.upload(file, applicationId, currentTravelerIndex, documentType);
      
      // Remove any previously uploaded doc of same type for same traveler
      const filtered = documents.filter(d => !(d.document_type === documentType && d.traveler_index === currentTravelerIndex));
      filtered.push({
        document_id: uploadResponse.data.document_id, filename: file.name,
        document_type: documentType, traveler_index: currentTravelerIndex
      });
      setDocuments(filtered);
      
      const status = checkResponse?.data?.overall_status;
      if (status === 'pass') {
        toast.success(`${file.name} verified & uploaded!`);
      } else if (status === 'warning') {
        toast.warning(`${file.name} uploaded with warnings. Please review.`);
      } else {
        toast.success('Document uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    } finally {
      setCheckingDoc(null);
    }
  };

  const handleUrlUpload = async (url, documentType) => {
    if (!applicationId) {
      toast.error('Please complete the previous steps first');
      return;
    }
    if (!url || !url.trim()) {
      toast.error('Please enter a valid URL');
      return;
    }
    
    setCheckingDoc(documentType);
    try {
      // Fetch the image from URL and convert to a File object
      const response = await fetch(url);
      const blob = await response.blob();
      const filename = url.split('/').pop()?.split('?')[0] || 'document.jpg';
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      
      // Use existing file upload handler
      await handleFileUpload(file, documentType);
      setDocUrlInputs(prev => ({ ...prev, [`${documentType}_${currentTravelerIndex}`]: '' }));
      setDocUploadMode(prev => ({ ...prev, [`${documentType}_${currentTravelerIndex}`]: 'file' }));
    } catch (error) {
      console.error('URL upload error:', error);
      toast.error('Failed to fetch image from URL. Please check the URL or use file upload.');
      setCheckingDoc(null);
    }
  };

  const handleSendOtp = async () => {
    const phone = travelers[currentTravelerIndex]?.phone;
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid mobile number first');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await otpApi.send(phone, `wizard_${currentTravelerIndex}`);
      setOtpSent(true);
      setDemoOtp(res.data.demo_otp || '');
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Please enter 6-digit OTP');
      return;
    }
    setOtpLoading(true);
    try {
      await otpApi.verify(otpCode, `wizard_${currentTravelerIndex}`);
      setOtpVerified(true);
      toast.success('Mobile number verified!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const errors = {};
      if (!tripDetails.country_id) errors.country_id = 'Please select a destination country';
      if (!tripDetails.visa_type_id) errors.visa_type_id = 'Please select a visa type';
      if (!tripDetails.travel_date) errors.travel_date = 'Departure date is required';
      if (!tripDetails.return_date) errors.return_date = 'Return date is required';
      if (Object.keys(errors).length) {
        setFieldErrors(errors);
        setShakeTrigger((s) => s + 1);
        toast.error('Please fill in all required fields');
        focusFirstError(errors);
        return;
      }
      setFieldErrors({});
    }
    if (currentStep === 3) {
      const traveler = travelers[currentTravelerIndex];
      const errors = {};
      if (!traveler.first_name?.trim()) errors.first_name = 'First name is required';
      if (!traveler.last_name?.trim()) errors.last_name = 'Last name is required';
      if (!traveler.passport_number?.trim()) {
        errors.passport_number = 'Passport number is required';
      } else if (traveler.passport_number.trim().length < 5) {
        errors.passport_number = 'Passport number looks too short';
      }
      if (Object.keys(errors).length) {
        setFieldErrors(errors);
        setShakeTrigger((s) => s + 1);
        toast.error('Please fix the highlighted fields below');
        focusFirstError(errors);
        return;
      }
      setFieldErrors({});
    }
    if (currentStep === 3 && !applicationId) {
      setLoading(true);
      try {
        const response = await applicationsApi.create({
          country_id: tripDetails.country_id,
          visa_type_id: tripDetails.visa_type_id,
          travel_date: tripDetails.travel_date.toISOString().split('T')[0],
          return_date: tripDetails.return_date.toISOString().split('T')[0],
          purpose: tripDetails.purpose,
          num_travelers: tripDetails.num_travelers,
          travelers: travelers,
          total_fee: totalFee
        });
        setApplicationId(response.data.application_id);
        toast.success('Application created!');
      } catch (error) {
        console.error('Error creating application:', error);
        toast.error('Failed to create application');
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    setCurrentStep(currentStep + 1);
  };

  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [, setNowTick] = useState(0); // forces re-render for relative time
  const lastSnapshotRef = useRef('');

  const buildDraftPayload = () => ({
    application_id: applicationId || undefined,
    country_id: tripDetails.country_id || null,
    visa_type_id: tripDetails.visa_type_id || null,
    travel_date: tripDetails.travel_date ? tripDetails.travel_date.toISOString().split('T')[0] : null,
    return_date: tripDetails.return_date ? tripDetails.return_date.toISOString().split('T')[0] : null,
    purpose: tripDetails.purpose || null,
    num_travelers: tripDetails.num_travelers || 1,
    travelers: travelers,
    total_fee: totalFee || 0,
    current_step: currentStep,
    include_appointment: includeAppointment,
    express_processing: expressProcessing,
  });

  const performDraftSave = async (silent = false) => {
    if (!silent) setSavingDraft(true);
    try {
      const payload = buildDraftPayload();
      const res = await applicationsApi.saveDraft(payload);
      if (!applicationId && res.data?.application_id) {
        setApplicationId(res.data.application_id);
      }
      lastSnapshotRef.current = JSON.stringify({ ...payload, application_id: undefined });
      setLastSavedAt(new Date());
      if (!silent) {
        toast.success('Draft saved! You can resume any time from My Applications.');
        setTimeout(() => navigate('/my-applications'), 600);
      }
      return true;
    } catch (err) {
      console.error('Save draft error:', err);
      if (!silent) toast.error(err.response?.data?.detail || 'Failed to save draft');
      return false;
    } finally {
      if (!silent) setSavingDraft(false);
    }
  };

  const handleSaveDraft = () => performDraftSave(false);

  // Silent auto-save every 30s when state has actually changed (skip Step 5 / paid apps)
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentStep >= 5) return;
      if (savingDraft || loading) return;
      // Need at least one meaningful field to save
      const hasSomething = tripDetails.country_id || tripDetails.visa_type_id ||
        tripDetails.travel_date || (travelers[0] && (travelers[0].first_name || travelers[0].passport_number));
      if (!hasSomething) return;
      const payload = buildDraftPayload();
      const snapshot = JSON.stringify({ ...payload, application_id: undefined });
      if (snapshot === lastSnapshotRef.current) return; // unchanged, skip
      performDraftSave(true);
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripDetails, travelers, currentStep, includeAppointment, expressProcessing, applicationId, savingDraft, loading, totalFee]);

  // Tick every 20s so the relative "Last saved X ago" indicator stays fresh
  useEffect(() => {
    if (!lastSavedAt) return;
    const t = setInterval(() => setNowTick((n) => n + 1), 20000);
    return () => clearInterval(t);
  }, [lastSavedAt]);

  const formatLastSaved = () => {
    if (!lastSavedAt) return null;
    const seconds = Math.floor((Date.now() - lastSavedAt.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const orderResponse = await paymentsApi.createOrder(applicationId, totalFee, 'INR');
      const order = orderResponse.data;
      if (order.is_demo) {
        toast.info('Demo mode: Simulating payment...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        await paymentsApi.verify(order.order_id);
        toast.success('Payment completed successfully!');
        navigate(`/applications/${applicationId}`);
      } else {
        const options = {
          key: order.key_id, amount: order.amount * 100, currency: order.currency,
          order_id: order.order_id, name: 'EasyMyVisa',
          description: `Visa Application - ${selectedCountry?.name}`,
          handler: async (response) => {
            try {
              await paymentsApi.verify(order.order_id, response.razorpay_payment_id, response.razorpay_signature);
              toast.success('Payment completed successfully!');
              navigate(`/applications/${applicationId}`);
            } catch (error) { toast.error('Payment verification failed'); }
          },
          prefill: { name: user?.name, email: user?.email },
          theme: { color: '#FF5A36' }
        };
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // =================== RENDER STEPS ===================

  const renderStep1 = () => (
    <div className="space-y-6" data-testid="step-trip-details">
      {/* Country Selection */}
      <div className="wizard-card" data-error-key="country_id">
        <div className="wizard-card-header">
          <Globe className="w-5 h-5 text-[#FF5A36]" />
          <span>Select Your Destination</span>
        </div>
        <div className="p-5">
          <Select value={tripDetails.country_id} onValueChange={(value) => { setTripDetails({ ...tripDetails, country_id: value, visa_type_id: '' }); clearFieldError('country_id'); clearFieldError('visa_type_id'); }}>
            <SelectTrigger data-testid="country-select" className={errClass('country_id')} key={`country-${shakeTrigger}-${fieldErrors.country_id ? 1 : 0}`}>
              <SelectValue placeholder="Choose a country..." />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem key={country.country_id} value={country.country_id}>
                  <span className="inline-flex items-center gap-2"><CountryFlag code={country.code} size="sm" /> {country.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError name="country_id" />
        </div>
      </div>

      {/* Available Visa Types */}
      {tripDetails.country_id && visaTypes.length > 0 && (
        <div className={`wizard-card ${fieldErrors.visa_type_id ? 'field-error animate-field-shake' : ''}`} data-error-key="visa_type_id" key={`visa-${shakeTrigger}-${fieldErrors.visa_type_id ? 1 : 0}`}>
          <div className="wizard-card-header">
            <FileText className="w-5 h-5 text-[#FF5A36]" />
            <span>Available Visa Types</span>
            {fieldErrors.visa_type_id && (
              <span className="ml-auto text-xs font-medium text-red-600" data-testid="error-visa_type_id">{fieldErrors.visa_type_id}</span>
            )}
          </div>
          <div className="p-5 space-y-4">
            {visaTypes.map((visa) => {
              const isSelected = tripDetails.visa_type_id === visa.visa_type_id;
              return (
                <label
                  key={visa.visa_type_id}
                  className={`block rounded-xl border-2 cursor-pointer transition-all overflow-hidden ${
                    isSelected
                      ? 'border-[#FF5A36] shadow-md'
                      : 'border-[#E8E2D6] hover:border-[#FF5A36]/40'
                  }`}
                  data-testid={`visa-plan-${visa.visa_type_id}`}
                >
                  <input
                    type="radio"
                    name="visa_plan"
                    className="hidden"
                    checked={isSelected}
                    onChange={() => { setTripDetails({ ...tripDetails, visa_type_id: visa.visa_type_id }); clearFieldError('visa_type_id'); }}
                  />

                  {/* Top: Name + Price */}
                  <div className={`px-5 py-4 ${isSelected ? 'bg-[#FF5A36]/5' : 'bg-white'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            isSelected ? 'border-[#FF5A36]' : 'border-[#D1CBBF]'
                          }`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-[#FF5A36]" />}
                          </div>
                          <h4 className="font-bold text-[#0A1E40] text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{visa.name}</h4>
                          {visa.is_popular && (
                            <span className="bg-[#FF5A36] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">Popular</span>
                          )}
                        </div>
                        {visa.description && (
                          <p className="text-xs text-[#4A5568] mt-1 ml-6">{visa.description}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {visa.fee?.toLocaleString('en-IN', { style: 'currency', currency: visa.currency || 'INR', maximumFractionDigits: 0 })}
                        </p>
                        <p className="text-[10px] text-[#9CA3AF]">per person</p>
                      </div>
                    </div>
                  </div>

                  {/* Info Chips */}
                  <div className="px-5 py-3 border-t border-dashed border-[#E8E2D6] bg-[#FAFAF7]">
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-[#FF5A36]" />
                        <span className="text-[11px] text-[#4A5568]"><span className="font-semibold text-[#0A1E40]">{visa.processing_time}</span> processing</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5 text-[#FF5A36]" />
                        <span className="text-[11px] text-[#4A5568]">Valid <span className="font-semibold text-[#0A1E40]">{visa.validity}</span></span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-[#FF5A36]" />
                        <span className="text-[11px] text-[#4A5568]">Stay <span className="font-semibold text-[#0A1E40]">{visa.max_stay}</span></span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded: Documents + Eligibility (on select) */}
                  {isSelected && (
                    <div className="px-5 py-3 border-t border-[#E8E2D6] space-y-2.5">
                      {visa.requirements?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#4A5568] mb-1.5">Eligibility</p>
                          <div className="flex flex-wrap gap-1.5">
                            {visa.requirements.map((req, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-100 rounded-md text-[11px] text-emerald-700">
                                <CheckCircle className="w-3 h-3" /> {req}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {visa.documents_required?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#4A5568] mb-1.5">Documents Required</p>
                          <div className="flex flex-wrap gap-1.5">
                            {visa.documents_required.map((doc, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-100 rounded-md text-[11px] text-blue-700">
                                <FileText className="w-3 h-3" /> {doc}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Travel Dates */}
      <div className="wizard-card">
        <div className="wizard-card-header">
          <CalendarIcon className="w-5 h-5 text-[#FF5A36]" />
          <span>Travel Dates & Travelers</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div data-error-key="travel_date">
              <Label className="text-xs font-medium text-[#4A5568] mb-1.5 block">Departure Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={`w-full justify-start text-left font-normal h-11 ${errClass('travel_date')}`} data-testid="travel-date-btn" key={`tdate-${shakeTrigger}-${fieldErrors.travel_date ? 1 : 0}`}>
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#FF5A36]" />
                    {tripDetails.travel_date ? format(tripDetails.travel_date, 'dd MMM yyyy') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={tripDetails.travel_date} onSelect={(date) => { setTripDetails({ ...tripDetails, travel_date: date, return_date: null }); clearFieldError('travel_date'); setTimeout(() => setReturnDateOpen(true), 200); }} disabled={(date) => date < new Date()} /></PopoverContent>
              </Popover>
              <FieldError name="travel_date" />
            </div>
            <div data-error-key="return_date">
              <Label className="text-xs font-medium text-[#4A5568] mb-1.5 block">Return Date *</Label>
              <Popover open={returnDateOpen} onOpenChange={setReturnDateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={`w-full justify-start text-left font-normal h-11 ${errClass('return_date')}`} data-testid="return-date-btn" key={`rdate-${shakeTrigger}-${fieldErrors.return_date ? 1 : 0}`}>
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#FF5A36]" />
                    {tripDetails.return_date ? format(tripDetails.return_date, 'dd MMM yyyy') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={tripDetails.return_date} onSelect={(date) => { setTripDetails({ ...tripDetails, return_date: date }); clearFieldError('return_date'); setReturnDateOpen(false); }} disabled={(date) => date < (tripDetails.travel_date || new Date())} /></PopoverContent>
              </Popover>
              <FieldError name="return_date" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-[#4A5568] mb-1.5 block">Number of Travelers</Label>
              <div className="flex items-center gap-0 border border-[#D1CBBF] rounded-lg overflow-hidden h-11" data-testid="traveler-counter">
                <button
                  type="button"
                  onClick={() => setTripDetails({ ...tripDetails, num_travelers: Math.max(1, tripDetails.num_travelers - 1) })}
                  disabled={tripDetails.num_travelers <= 1}
                  className="w-12 h-full flex items-center justify-center bg-[#FF5A36] hover:bg-[#E0492B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="traveler-minus-btn"
                >
                  <Minus className="w-4 h-4 text-white" />
                </button>
                <div className="flex-1 text-center font-semibold text-[#0A1E40] text-lg" data-testid="traveler-count">
                  {tripDetails.num_travelers}
                </div>
                <button
                  type="button"
                  onClick={() => setTripDetails({ ...tripDetails, num_travelers: Math.min(10, tripDetails.num_travelers + 1) })}
                  disabled={tripDetails.num_travelers >= 10}
                  className="w-12 h-full flex items-center justify-center bg-[#FF5A36] hover:bg-[#E0492B] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="traveler-plus-btn"
                >
                  <Plus className="w-4 h-4 text-white" />
                </button>
              </div>
              <p className="text-[10px] text-[#9CA3AF] mt-1">Max 10 travelers per application</p>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#4A5568] mb-1.5 block">Purpose of Visit</Label>
              <Select value={tripDetails.purpose} onValueChange={(value) => setTripDetails({ ...tripDetails, purpose: value })}>
                <SelectTrigger data-testid="purpose-select"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tourism">Tourism / Leisure</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="medical">Medical</SelectItem>
                  <SelectItem value="education">Education</SelectItem>
                  <SelectItem value="transit">Transit</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Booking (if available for this visa type) */}
      {selectedVisa?.appointment_enabled && (
        <div className="wizard-card overflow-hidden" data-testid="appointment-section">
          <div
            className={`p-5 cursor-pointer transition-all ${includeAppointment ? 'bg-[#FF5A36]' : 'bg-white border border-[#D1CBBF]'}`}
            onClick={() => setIncludeAppointment(!includeAppointment)}
            data-testid="appointment-toggle"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${includeAppointment ? 'bg-white/20' : 'bg-[#FF5A36]/10'}`}>
                  <CalendarIcon className={`w-5 h-5 ${includeAppointment ? 'text-white' : 'text-[#FF5A36]'}`} />
                </div>
                <div>
                  <h4 className={`font-semibold ${includeAppointment ? 'text-white' : 'text-[#0A1E40]'}`}>
                    Have you paid {selectedCountry?.name || 'country'} visa fees yet?
                  </h4>
                  <p className={`text-xs ${includeAppointment ? 'text-white/70' : 'text-[#4A5568]'}`}>
                    Book biometric/interview appointment · {selectedVisa.appointment_duration || '15-30 mins'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <p className={`text-sm font-bold ${includeAppointment ? 'text-white' : 'text-[#0A1E40]'}`}>
                  +₹{(selectedVisa.appointment_fee || 0).toLocaleString()}/person
                </p>
                <div className={`w-12 h-7 rounded-full transition-all ${includeAppointment ? 'bg-white/30' : 'bg-[#D1CBBF]'}`}>
                  <div className={`w-5 h-5 rounded-full mt-1 transition-all ${includeAppointment ? 'bg-white ml-6' : 'bg-white ml-1'}`} />
                </div>
              </div>
            </div>
          </div>
          {includeAppointment && (
            <div className="px-5 pb-4 pt-2 bg-[#FF5A36]">
              <p className="text-white/70 text-xs">Appointment will be scheduled after visa application submission. You'll receive date & time confirmation via email.</p>
            </div>
          )}
        </div>
      )}

      {/* Express Processing */}
      <div className="wizard-card overflow-hidden">
        <div
          className={`p-5 cursor-pointer transition-all ${expressProcessing ? 'bg-[#0A1E40]' : 'bg-gradient-to-r from-[#0A1E40] to-[#1A3A6A]'}`}
          onClick={() => setExpressProcessing(!expressProcessing)}
          data-testid="express-toggle"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF5A36] flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-white">Express Processing</h4>
                <p className="text-white/60 text-xs">Get your visa 3x faster with priority queue</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-white/70 text-sm">+50% fee</p>
              <div className={`w-12 h-7 rounded-full transition-all ${expressProcessing ? 'bg-[#FF5A36]' : 'bg-white/20'}`}>
                <div className={`w-5 h-5 bg-white rounded-full mt-1 transition-all ${expressProcessing ? 'ml-6' : 'ml-1'}`} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const frontKey = `${currentTravelerIndex}_front`;
    const backKey = `${currentTravelerIndex}_back`;
    const frontPage = passportPages[frontKey];
    const backPage = passportPages[backKey];
    const frontVerified = frontPage?.status === 'verified';
    const backVerified = backPage?.status === 'verified';

    return (
      <div className="space-y-6" data-testid="step-passport-scan">
        {/* Title Section */}
        <div className="bg-white rounded-2xl border border-[#D1CBBF] p-6">
          <h2 className="text-xl font-bold text-[#0A1E40] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Personal Information
          </h2>
          <p className="text-[#4A5568]">Scan your passport to auto-fill details with AI verification</p>
        </div>

        {/* Traveler Tabs */}
        {tripDetails.num_travelers > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#4A5568]">Scanning for:</span>
            <div className="flex gap-1.5">
              {travelers.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setCurrentTravelerIndex(i); setActivePassportStep('front'); }}
                  className={`w-9 h-9 rounded-full text-sm font-semibold transition-all ${
                    i === currentTravelerIndex
                      ? 'bg-[#FF5A36] text-white shadow-md scale-110'
                      : travelers[i].passport_number
                        ? 'bg-green-500 text-white'
                        : 'bg-[#F5F0E6] text-[#4A5568] border border-[#D1CBBF]'
                  }`}
                  data-testid={`traveler-tab-${i}`}
                >
                  {travelers[i].passport_number ? <CheckCircle className="w-4 h-4 mx-auto" /> : i + 1}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* AI Document Verification Header Bar */}
        <div className="rounded-2xl overflow-hidden" data-testid="ai-verification-bar">
          <div className="bg-gradient-to-r from-[#0A1E40] to-[#1A3A6A] p-5">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-white/80" />
              <h3 className="font-bold text-white text-lg">AI Document Verification</h3>
            </div>
            
            {/* Front/Back Progress Indicator */}
            <div className="flex items-center gap-0">
              <button 
                onClick={() => setActivePassportStep('front')}
                className="flex items-center gap-2 shrink-0"
                data-testid="passport-step-front"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  frontVerified ? 'bg-green-500 text-white' : 'bg-[#FF5A36] text-white'
                }`}>
                  {frontVerified ? <CheckCircle className="w-5 h-5" /> : '1'}
                </div>
                <span className="text-white font-medium text-sm">Front Page</span>
              </button>
              
              <div className="flex-1 mx-4 h-1 rounded-full bg-white/20 overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${
                  frontVerified ? 'bg-green-400 w-full' : 'bg-white/30 w-0'
                }`} style={{ width: frontVerified ? '100%' : '0%' }} />
              </div>
              
              <button 
                onClick={() => setActivePassportStep('back')}
                className="flex items-center gap-2 shrink-0"
                data-testid="passport-step-back"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  backVerified ? 'bg-green-500 text-white' : activePassportStep === 'back' ? 'bg-[#FF5A36] text-white' : 'bg-white/20 text-white'
                }`}>
                  {backVerified ? <CheckCircle className="w-5 h-5" /> : '2'}
                </div>
                <span className="text-white font-medium text-sm">Back Page</span>
              </button>
            </div>
          </div>
        </div>

        {/* Scanning Overlay */}
        {scanningPassport && (
          <div className="p-8 bg-[#F5F0E6] rounded-2xl text-center border border-[#D1CBBF]">
            <Loader2 className="w-12 h-12 animate-spin text-[#FF5A36] mx-auto mb-4" />
            <p className="font-semibold text-[#0A1E40] text-lg">Scanning passport with AI...</p>
            <p className="text-sm text-[#4A5568] mt-1">Extracting name, passport number, dates & more</p>
          </div>
        )}

        {/* Front Page & Back Page Cards - Side by Side */}
        {!scanningPassport && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Step 1: Front Page */}
            <div className={`rounded-2xl border-2 overflow-hidden transition-all ${
              frontVerified ? 'border-green-400' : activePassportStep === 'front' ? 'border-[#FF5A36]' : 'border-[#D1CBBF]'
            }`} data-testid="front-page-card">
              {/* Card Header */}
              <div className="px-5 py-3 bg-white border-b border-[#F5F0E6] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm ${frontVerified ? 'text-green-700' : 'text-[#0A1E40]'}`}>
                    Step 1: Front Page
                  </span>
                  {frontVerified && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold" data-testid="front-verified-badge">
                      <CheckCircle className="w-3.5 h-3.5" /> Verified
                    </span>
                  )}
                </div>
                {frontPage?.preview && (
                  <button 
                    onClick={() => { 
                      setPassportPages(prev => { const n = {...prev}; delete n[frontKey]; return n; }); 
                      setActivePassportStep('front');
                    }}
                    className="text-[#4A5568] hover:text-[#FF5A36] transition-colors"
                    title="Re-scan"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>
                  </button>
                )}
              </div>
              
              {/* Card Body */}
              <div className="bg-white p-4">
                {frontPage?.preview ? (
                  <div className="relative">
                    <img 
                      src={frontPage.preview} 
                      alt="Passport Front" 
                      className="w-full h-56 object-contain rounded-xl border border-[#F5F0E6] bg-[#FAFAF5]"
                    />
                    {/* Authenticity Bar */}
                    {frontVerified && (
                      <div className="mt-3 bg-green-600 rounded-lg px-4 py-2.5 flex items-center justify-between" data-testid="authenticity-bar">
                        <div className="flex items-center gap-2 text-white">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-semibold">Authenticity: {frontPage.authenticity || 95}%</span>
                        </div>
                        <div className="flex gap-0.5">
                          {[...Array(10)].map((_, i) => (
                            <div key={i} className={`w-1.5 h-3 rounded-sm ${i < Math.round((frontPage.authenticity || 95) / 10) ? 'bg-white' : 'bg-white/30'}`} />
                          ))}
                        </div>
                      </div>
                    )}
                    {frontPage?.status === 'failed' && (
                      <div className="mt-3 bg-red-500 rounded-lg px-4 py-2.5 flex items-center gap-2 text-white">
                        <XCircle className="w-4 h-4" />
                        <span className="text-sm font-semibold">Verification failed - Please re-upload</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <input type="file" accept="image/*" className="hidden" id={`passport-front-${currentTravelerIndex}`}
                      onChange={(e) => e.target.files?.[0] && handlePassportScan(e.target.files[0], 'front')} />
                    <label htmlFor={`passport-front-${currentTravelerIndex}`}
                      className="block border-2 border-dashed border-[#D1CBBF] hover:border-[#FF5A36] rounded-xl p-8 text-center cursor-pointer transition-all hover:bg-[#FF5A36]/5"
                      data-testid="passport-front-upload"
                    >
                      <Upload className="w-8 h-8 text-[#9CA3AF] mx-auto mb-3" />
                      <p className="font-medium text-[#4A5568]">Upload Image</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">JPG, PNG, PDF</p>
                    </label>
                    
                    <label htmlFor={`passport-front-${currentTravelerIndex}`}
                      className="mt-3 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-[#D1CBBF] hover:border-[#FF5A36] cursor-pointer transition-all text-[#4A5568] hover:text-[#FF5A36]"
                    >
                      <Camera className="w-5 h-5" />
                      <span className="font-medium text-sm">Use Camera</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Back Page */}
            <div className={`rounded-2xl border-2 overflow-hidden transition-all ${
              backVerified ? 'border-green-400' : activePassportStep === 'back' ? 'border-[#FF5A36]' : 'border-[#D1CBBF]'
            }`} data-testid="back-page-card">
              {/* Card Header */}
              <div className="px-5 py-3 bg-white border-b border-[#F5F0E6] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm ${backVerified ? 'text-green-700' : 'text-[#0A1E40]'}`}>
                    Step 2: Back Page
                  </span>
                  {backVerified && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold" data-testid="back-verified-badge">
                      <CheckCircle className="w-3.5 h-3.5" /> Verified
                    </span>
                  )}
                </div>
                {backPage?.preview && (
                  <button 
                    onClick={() => { 
                      setPassportPages(prev => { const n = {...prev}; delete n[backKey]; return n; }); 
                      setActivePassportStep('back');
                    }}
                    className="text-[#4A5568] hover:text-[#FF5A36] transition-colors"
                    title="Re-scan"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>
                  </button>
                )}
              </div>
              
              {/* Card Body */}
              <div className="bg-white p-4">
                {backPage?.preview ? (
                  <div className="relative">
                    <img 
                      src={backPage.preview} 
                      alt="Passport Back" 
                      className="w-full h-56 object-contain rounded-xl border border-[#F5F0E6] bg-[#FAFAF5]"
                    />
                    {backVerified && (
                      <div className="mt-3 bg-green-600 rounded-lg px-4 py-2.5 flex items-center justify-between" data-testid="back-authenticity-bar">
                        <div className="flex items-center gap-2 text-white">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-semibold">Authenticity: {backPage.authenticity || 90}%</span>
                        </div>
                        <div className="flex gap-0.5">
                          {[...Array(10)].map((_, i) => (
                            <div key={i} className={`w-1.5 h-3 rounded-sm ${i < Math.round((backPage.authenticity || 90) / 10) ? 'bg-white' : 'bg-white/30'}`} />
                          ))}
                        </div>
                      </div>
                    )}
                    {backPage?.status === 'failed' && (
                      <div className="mt-3 bg-red-500 rounded-lg px-4 py-2.5 flex items-center gap-2 text-white">
                        <XCircle className="w-4 h-4" />
                        <span className="text-sm font-semibold">Verification failed - Please re-upload</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <input type="file" accept="image/*" className="hidden" id={`passport-back-${currentTravelerIndex}`}
                      onChange={(e) => e.target.files?.[0] && handlePassportScan(e.target.files[0], 'back')} />
                    <label htmlFor={`passport-back-${currentTravelerIndex}`}
                      className="block border-2 border-dashed border-[#D1CBBF] hover:border-[#FF5A36] rounded-xl p-8 text-center cursor-pointer transition-all hover:bg-[#FF5A36]/5"
                      data-testid="passport-back-upload"
                    >
                      <Upload className="w-8 h-8 text-[#9CA3AF] mx-auto mb-3" />
                      <p className="font-medium text-[#4A5568]">Upload Image</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">JPG, PNG, PDF</p>
                    </label>
                    
                    <label htmlFor={`passport-back-${currentTravelerIndex}`}
                      className="mt-3 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-[#D1CBBF] hover:border-[#FF5A36] cursor-pointer transition-all text-[#4A5568] hover:text-[#FF5A36]"
                    >
                      <Camera className="w-5 h-5" />
                      <span className="font-medium text-sm">Use Camera</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Extracted Data Preview */}
        {travelers[currentTravelerIndex].passport_number && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-5" data-testid="scan-success">
            <div className="flex items-center gap-2 text-green-700 mb-3">
              <CheckCircle className="w-5 h-5" />
              <span className="font-bold">Passport Data Extracted Successfully</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-white rounded-xl p-3 border border-green-200">
                <p className="text-green-600/70 text-[10px] uppercase tracking-wider mb-0.5">Name</p>
                <p className="font-semibold text-green-800">{travelers[currentTravelerIndex].first_name} {travelers[currentTravelerIndex].last_name}</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-green-200">
                <p className="text-green-600/70 text-[10px] uppercase tracking-wider mb-0.5">Passport No.</p>
                <p className="font-semibold text-green-800">{travelers[currentTravelerIndex].passport_number}</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-green-200">
                <p className="text-green-600/70 text-[10px] uppercase tracking-wider mb-0.5">Date of Birth</p>
                <p className="font-semibold text-green-800">{travelers[currentTravelerIndex].dob || '-'}</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-green-200">
                <p className="text-green-600/70 text-[10px] uppercase tracking-wider mb-0.5">Expiry Date</p>
                <p className="font-semibold text-green-800">{travelers[currentTravelerIndex].passport_expiry || '-'}</p>
              </div>
            </div>
            {/* Passport Expiry Warning */}
            {(() => {
              const expiry = travelers[currentTravelerIndex].passport_expiry;
              if (!expiry) return null;
              const expiryDate = new Date(expiry);
              if (isNaN(expiryDate.getTime())) return null;
              const referenceDate = tripDetails.travel_date ? new Date(tripDetails.travel_date) : new Date();
              const sixMonthsAfter = new Date(referenceDate);
              sixMonthsAfter.setMonth(sixMonthsAfter.getMonth() + 6);
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              if (expiryDate < today) {
                return (
                  <div className="mt-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-3.5" data-testid="passport-expired-warning">
                    <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-red-800">Passport has expired</p>
                      <p className="text-xs text-red-700 mt-0.5">Your passport expired on <span className="font-semibold">{expiry}</span>. Please renew it before applying for a visa.</p>
                    </div>
                  </div>
                );
              }
              if (expiryDate < sixMonthsAfter) {
                const daysToExpiry = Math.ceil((expiryDate - referenceDate) / (1000 * 60 * 60 * 24));
                const months = Math.max(0, Math.floor(daysToExpiry / 30));
                return (
                  <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3.5" data-testid="passport-expiry-warning">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-amber-800">Passport expires soon — visa may be rejected</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Most countries require passports to be valid for at least <span className="font-semibold">6 months past your travel date</span>.
                        Yours expires in <span className="font-semibold">{months > 0 ? `~${months} month${months > 1 ? 's' : ''}` : `${daysToExpiry} day${daysToExpiry !== 1 ? 's' : ''}`}</span>
                        {tripDetails.travel_date && <> (from your travel date)</>}.
                        Consider renewing before submitting.
                      </p>
                    </div>
                  </div>
                );
              }
              return (
                <div className="mt-4 flex items-center gap-2 text-xs text-green-700" data-testid="passport-expiry-ok">
                  <CheckCircle className="w-4 h-4" />
                  <span>Passport validity meets the standard 6-month rule.</span>
                </div>
              );
            })()}
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-[#4A5568] px-1">
          <Info className="w-4 h-4 shrink-0" />
          <span>You can skip scanning and enter details manually in the next step.</span>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const traveler = travelers[currentTravelerIndex];
    return (
      <div className="space-y-5" data-testid="step-personal-info">
        {/* Traveler Tabs */}
        {tripDetails.num_travelers > 1 && (
          <div className="flex gap-2 mb-2">
            {travelers.map((t, i) => (
              <button
                key={i} onClick={() => setCurrentTravelerIndex(i)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  i === currentTravelerIndex ? 'bg-[#FF5A36] text-white' : 'bg-white text-[#4A5568] border border-[#D1CBBF]'
                }`}
                data-testid={`traveler-btn-${i}`}
              >
                Traveler {i + 1} {t.first_name && `(${t.first_name})`}
              </button>
            ))}
          </div>
        )}

        {/* Passport Details */}
        <div className="wizard-card">
          <div className="wizard-card-header">
            <FileCheck className="w-5 h-5 text-[#FF5A36]" />
            <span>Passport Details</span>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div data-error-key="first_name"><Label className="wizard-label">First Name *</Label><Input value={traveler.first_name} onChange={(e) => handleTravelerChange('first_name', e.target.value)} placeholder="As in passport" data-testid="first-name-input" className={errClass('first_name')} key={`fn-${shakeTrigger}-${fieldErrors.first_name ? 1 : 0}`} /><FieldError name="first_name" /></div>
              <div data-error-key="last_name"><Label className="wizard-label">Last Name *</Label><Input value={traveler.last_name} onChange={(e) => handleTravelerChange('last_name', e.target.value)} placeholder="As in passport" data-testid="last-name-input" className={errClass('last_name')} key={`ln-${shakeTrigger}-${fieldErrors.last_name ? 1 : 0}`} /><FieldError name="last_name" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div data-error-key="passport_number"><Label className="wizard-label">Passport Number *</Label><Input value={traveler.passport_number} onChange={(e) => handleTravelerChange('passport_number', e.target.value.toUpperCase())} placeholder="e.g., A1234567" data-testid="passport-input" className={errClass('passport_number')} key={`pn-${shakeTrigger}-${fieldErrors.passport_number ? 1 : 0}`} /><FieldError name="passport_number" /></div>
              <div><Label className="wizard-label">Issue Date</Label><Input type="date" value={traveler.passport_issue_date} onChange={(e) => handleTravelerChange('passport_issue_date', e.target.value)} /></div>
              <div><Label className="wizard-label">Expiry Date *</Label><Input type="date" value={traveler.passport_expiry} onChange={(e) => handleTravelerChange('passport_expiry', e.target.value)} data-testid="passport-expiry-input" /></div>
            </div>
          </div>
        </div>

        {/* Personal Info */}
        <div className="wizard-card">
          <div className="wizard-card-header">
            <User className="w-5 h-5 text-[#FF5A36]" />
            <span>Personal Information</span>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="wizard-label">Date of Birth *</Label><Input type="date" value={traveler.dob} onChange={(e) => handleTravelerChange('dob', e.target.value)} data-testid="dob-input" /></div>
              <div>
                <Label className="wizard-label">Gender *</Label>
                <Select value={traveler.gender} onValueChange={(v) => handleTravelerChange('gender', v)}>
                  <SelectTrigger data-testid="gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Male</SelectItem>
                    <SelectItem value="F">Female</SelectItem>
                    <SelectItem value="O">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="wizard-label">Nationality *</Label><Input value={traveler.nationality} onChange={(e) => handleTravelerChange('nationality', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="wizard-label">Occupation</Label><Input value={traveler.occupation} onChange={(e) => handleTravelerChange('occupation', e.target.value)} placeholder="e.g., Software Engineer" /></div>
              <div><Label className="wizard-label">Nationality *</Label><Input value={traveler.nationality} onChange={(e) => handleTravelerChange('nationality', e.target.value)} /></div>
            </div>
          </div>
        </div>

        {/* Contact Information with OTP */}
        <div className="wizard-card">
          <div className="wizard-card-header">
            <Smartphone className="w-5 h-5 text-[#FF5A36]" />
            <span>Contact Information</span>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="wizard-label">Mobile Number *</Label>
                <div className="flex gap-2">
                  <Input
                    value={traveler.phone}
                    onChange={(e) => { handleTravelerChange('phone', e.target.value); if (otpSent) { setOtpSent(false); setOtpVerified(false); setOtpCode(''); setDemoOtp(''); } }}
                    placeholder="+91 XXXXX XXXXX"
                    className="flex-1"
                    data-testid="contact-phone-input"
                  />
                  {!otpVerified && (
                    <Button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={otpLoading || !traveler.phone || traveler.phone.length < 10}
                      className="bg-[#FF5A36] hover:bg-[#E0492B] text-white shrink-0 px-4"
                      data-testid="send-otp-btn"
                    >
                      {otpLoading && !otpSent ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                      {otpSent ? 'Resend' : 'Send OTP'}
                    </Button>
                  )}
                  {otpVerified && (
                    <div className="flex items-center gap-1.5 px-3 bg-green-50 border border-green-200 rounded-lg shrink-0">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-semibold text-green-700">Verified</span>
                    </div>
                  )}
                </div>
                {/* OTP Input */}
                {otpSent && !otpVerified && (
                  <div className="mt-3 p-3 bg-[#F5F0E6] rounded-xl space-y-2">
                    {demoOtp && (
                      <p className="text-xs text-[#9CA3AF]">
                        Demo OTP: <span className="font-bold text-[#0A1E40]">{demoOtp}</span>
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="Enter 6-digit OTP"
                        className="max-w-[180px] text-center text-lg tracking-widest font-mono bg-white"
                        data-testid="otp-code-input"
                      />
                      <Button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={otpLoading || otpCode.length !== 6}
                        className="bg-[#0A1E40] hover:bg-[#0A1E40]/90 text-white"
                        data-testid="verify-otp-btn"
                      >
                        {otpLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                        Verify
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label className="wizard-label">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input
                    type="email"
                    value={traveler.email}
                    onChange={(e) => handleTravelerChange('email', e.target.value)}
                    placeholder="email@example.com"
                    className="pl-10"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="wizard-card">
          <div className="wizard-card-header" onClick={() => toggleSection('address')} style={{cursor:'pointer'}}>
            <MapPin className="w-5 h-5 text-[#FF5A36]" />
            <span>Address Details</span>
            {expandedSections.address ? <ChevronUp className="w-4 h-4 ml-auto text-[#4A5568]" /> : <ChevronDown className="w-4 h-4 ml-auto text-[#4A5568]" />}
          </div>
          {(expandedSections.address !== false) && (
            <div className="p-5 space-y-4">
              <div><Label className="wizard-label">Full Address</Label><Textarea value={traveler.address} onChange={(e) => handleTravelerChange('address', e.target.value)} placeholder="Street address..." rows={2} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label className="wizard-label">City</Label><Input value={traveler.city} onChange={(e) => handleTravelerChange('city', e.target.value)} /></div>
                <div><Label className="wizard-label">State</Label><Input value={traveler.state} onChange={(e) => handleTravelerChange('state', e.target.value)} /></div>
                <div><Label className="wizard-label">Pincode</Label><Input value={traveler.pincode} onChange={(e) => handleTravelerChange('pincode', e.target.value)} /></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    const docTypes = selectedVisa?.documents_required?.length > 0
      ? selectedVisa.documents_required.map(d => ({ type: d.toLowerCase().replace(/\s+/g, '_'), label: d }))
      : [
          { type: 'passport', label: 'Passport Copy' },
          { type: 'photo', label: 'Passport Size Photo' },
          { type: 'bank_statement', label: 'Bank Statement' },
          { type: 'travel_insurance', label: 'Travel Insurance' },
          { type: 'itinerary', label: 'Travel Itinerary' },
          { type: 'other', label: 'Other Documents' }
        ];

    return (
      <div className="space-y-5" data-testid="step-documents">
        {tripDetails.num_travelers > 1 && (
          <div className="flex gap-2 mb-2">
            {travelers.map((t, i) => (
              <button
                key={i} onClick={() => setCurrentTravelerIndex(i)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  i === currentTravelerIndex ? 'bg-[#FF5A36] text-white' : 'bg-white text-[#4A5568] border border-[#D1CBBF]'
                }`}
              >
                Traveler {i + 1} {t.first_name && `(${t.first_name})`}
              </button>
            ))}
          </div>
        )}

        {/* Auto-Check Info Banner */}
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-blue-800 text-sm">AI Document Auto-Check</p>
            <p className="text-xs text-blue-600">Each document is instantly verified for quality, completeness, and compliance before submission.</p>
          </div>
        </div>

        <div className="wizard-card">
          <div className="wizard-card-header">
            <Upload className="w-5 h-5 text-[#FF5A36]" />
            <span>Upload Documents</span>
            <span className="ml-auto text-xs text-[#4A5568]">
              {documents.filter(d => d.traveler_index === currentTravelerIndex).length} / {docTypes.length} uploaded
            </span>
          </div>
          <div className="p-5">
            <div className="space-y-3">
              {docTypes.map((docType) => {
                const uploaded = documents.find(d => d.document_type === docType.type && d.traveler_index === currentTravelerIndex);
                const checkKey = `${docType.type}_${currentTravelerIndex}`;
                const checkResult = docCheckResults[checkKey];
                const isChecking = checkingDoc === docType.type;

                return (
                  <div key={docType.type} data-testid={`doc-item-${docType.type}`}>
                    <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      uploaded
                        ? checkResult?.overall_status === 'fail'
                          ? 'border-red-200 bg-red-50'
                          : checkResult?.overall_status === 'warning'
                            ? 'border-amber-200 bg-amber-50'
                            : 'border-green-200 bg-green-50'
                        : 'border-[#D1CBBF] bg-white hover:border-[#FF5A36]/50'
                    }`}>
                      <div className="flex items-center gap-3">
                        {isChecking ? (
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                          </div>
                        ) : uploaded ? (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            checkResult?.overall_status === 'fail' ? 'bg-red-100'
                              : checkResult?.overall_status === 'warning' ? 'bg-amber-100' : 'bg-green-100'
                          }`}>
                            {checkResult?.overall_status === 'fail'
                              ? <XCircle className="w-5 h-5 text-red-600" />
                              : checkResult?.overall_status === 'warning'
                                ? <AlertCircle className="w-5 h-5 text-amber-600" />
                                : <CheckCircle className="w-5 h-5 text-green-600" />}
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#F5F0E6] flex items-center justify-center">
                            <FileText className="w-5 h-5 text-[#4A5568]" />
                          </div>
                        )}
                        <div>
                          <p className={`font-medium text-sm ${
                            isChecking ? 'text-blue-700'
                              : uploaded
                                ? checkResult?.overall_status === 'fail' ? 'text-red-700'
                                  : checkResult?.overall_status === 'warning' ? 'text-amber-700' : 'text-green-700'
                                : 'text-[#0A1E40]'
                          }`}>
                            {docType.label}
                          </p>
                          {isChecking ? (
                            <p className="text-xs text-blue-600">Checking document quality...</p>
                          ) : uploaded ? (
                            <p className={`text-xs ${
                              checkResult?.overall_status === 'fail' ? 'text-red-600'
                                : checkResult?.overall_status === 'warning' ? 'text-amber-600' : 'text-green-600'
                            }`}>
                              {checkResult?.overall_message || uploaded.filename}
                            </p>
                          ) : (
                            <p className="text-xs text-[#4A5568]">PDF, JPG, PNG (Max 5MB)</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {uploaded && checkResult && (
                          <button
                            onClick={() => toggleSection(checkKey)}
                            className="text-xs text-[#4A5568] hover:text-[#0A1E40] underline"
                            data-testid={`view-report-${docType.type}`}
                          >
                            {expandedSections[checkKey] ? 'Hide' : 'Report'}
                          </button>
                        )}
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" id={`doc-${docType.type}-${currentTravelerIndex}`}
                          onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], docType.type)} />
                        <label htmlFor={`doc-${docType.type}-${currentTravelerIndex}`} className="cursor-pointer">
                          <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            uploaded
                              ? checkResult?.overall_status === 'fail'
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-[#FF5A36] text-white hover:bg-[#E0492B]'
                          }`}>
                            <Upload className="w-4 h-4" />
                            {uploaded ? (checkResult?.overall_status === 'fail' ? 'Re-upload' : 'Replace') : 'Upload'}
                          </span>
                        </label>
                      </div>
                    </div>
                    
                    {/* URL Upload Option */}
                    {!uploaded && !isChecking && (
                      <div className="mt-2 ml-4" data-testid={`url-upload-${docType.type}`}>
                        {docUploadMode[`${docType.type}_${currentTravelerIndex}`] === 'url' ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="url"
                              placeholder="Paste image URL here..."
                              value={docUrlInputs[`${docType.type}_${currentTravelerIndex}`] || ''}
                              onChange={(e) => setDocUrlInputs(prev => ({ ...prev, [`${docType.type}_${currentTravelerIndex}`]: e.target.value }))}
                              className="flex-1 h-9 text-sm"
                              data-testid={`url-input-${docType.type}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleUrlUpload(docUrlInputs[`${docType.type}_${currentTravelerIndex}`], docType.type)}
                              className="bg-[#FF5A36] hover:bg-[#E0492B] text-white h-9 px-3"
                              data-testid={`url-submit-${docType.type}`}
                            >
                              <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                            </Button>
                            <button
                              onClick={() => setDocUploadMode(prev => ({ ...prev, [`${docType.type}_${currentTravelerIndex}`]: 'file' }))}
                              className="text-xs text-[#4A5568] hover:text-[#FF5A36] underline whitespace-nowrap"
                            >
                              Use file
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDocUploadMode(prev => ({ ...prev, [`${docType.type}_${currentTravelerIndex}`]: 'url' }))}
                            className="text-xs text-[#FF5A36] hover:underline flex items-center gap-1"
                            data-testid={`switch-url-${docType.type}`}
                          >
                            <Globe className="w-3 h-3" /> Or upload via URL
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* Auto-Check Report Expandable */}
                    {expandedSections[checkKey] && checkResult?.checks && (
                      <div className="mt-2 ml-4 p-4 bg-white rounded-xl border border-[#D1CBBF] space-y-2" data-testid={`check-report-${docType.type}`}>
                        <p className="text-xs font-semibold text-[#0A1E40] mb-2 flex items-center gap-1.5">
                          <ShieldCheck className="w-4 h-4 text-[#FF5A36]" /> Auto-Check Report
                        </p>
                        {checkResult.checks.map((check, ci) => (
                          <div key={ci} className="flex items-start gap-2 text-xs">
                            {check.status === 'pass' ? (
                              <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                            ) : check.status === 'warning' ? (
                              <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                            )}
                            <div>
                              <span className="font-medium text-[#0A1E40]">{check.check_name}: </span>
                              <span className="text-[#4A5568]">{check.message}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm text-[#4A5568] px-1">
          <Info className="w-4 h-4 shrink-0" />
          <span>Accepted formats: PDF, JPG, PNG. Max 5MB. Documents are auto-verified by AI for quality and compliance.</span>
        </div>
      </div>
    );
  };

  const renderStep5 = () => (
    <div className="space-y-5" data-testid="step-review">
      {/* Trip Summary */}
      <div className="wizard-card">
        <div className="wizard-card-header cursor-pointer" onClick={() => toggleSection('trip')}>
          <Globe className="w-5 h-5 text-[#FF5A36]" />
          <span>Trip Details</span>
          <button className="ml-auto text-xs text-[#FF5A36] font-medium" onClick={(e) => { e.stopPropagation(); setCurrentStep(1); }}>Edit</button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div><p className="text-[#4A5568] text-xs mb-0.5">Destination</p><p className="font-medium text-[#0A1E40] inline-flex items-center gap-1"><CountryFlag code={selectedCountry?.code} size="sm" /> {selectedCountry?.name}</p></div>
            <div><p className="text-[#4A5568] text-xs mb-0.5">Visa Type</p><p className="font-medium text-[#0A1E40]">{selectedVisa?.name}</p></div>
            <div><p className="text-[#4A5568] text-xs mb-0.5">Processing</p><p className="font-medium text-[#0A1E40]">{expressProcessing ? 'Express' : 'Standard'}</p></div>
            <div><p className="text-[#4A5568] text-xs mb-0.5">Travel Date</p><p className="font-medium text-[#0A1E40]">{tripDetails.travel_date?.toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'})}</p></div>
            <div><p className="text-[#4A5568] text-xs mb-0.5">Return Date</p><p className="font-medium text-[#0A1E40]">{tripDetails.return_date?.toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'})}</p></div>
            <div><p className="text-[#4A5568] text-xs mb-0.5">Travelers</p><p className="font-medium text-[#0A1E40]">{tripDetails.num_travelers}</p></div>
          </div>
        </div>
      </div>

      {/* Travelers */}
      <div className="wizard-card">
        <div className="wizard-card-header">
          <User className="w-5 h-5 text-[#FF5A36]" />
          <span>Travelers ({travelers.length})</span>
          <button className="ml-auto text-xs text-[#FF5A36] font-medium" onClick={() => setCurrentStep(3)}>Edit</button>
        </div>
        <div className="p-5 space-y-3">
          {travelers.map((t, i) => (
            <div key={i} className="flex items-center justify-between p-4 bg-[#F5F0E6] rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0A1E40] text-white flex items-center justify-center text-sm font-bold">{i + 1}</div>
                <div>
                  <p className="font-medium text-[#0A1E40]">{t.first_name} {t.last_name}</p>
                  <p className="text-xs text-[#4A5568]">Passport: {t.passport_number} | DOB: {t.dob}</p>
                </div>
              </div>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          ))}
        </div>
      </div>

      {/* Documents */}
      <div className="wizard-card">
        <div className="wizard-card-header">
          <FileText className="w-5 h-5 text-[#FF5A36]" />
          <span>Documents ({documents.length})</span>
          <button className="ml-auto text-xs text-[#FF5A36] font-medium" onClick={() => setCurrentStep(4)}>Edit</button>
        </div>
        <div className="p-5">
          {documents.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {documents.map((doc, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-green-50 rounded-lg text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                  <span className="truncate text-green-700">{doc.filename}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#4A5568]">No documents uploaded</p>
          )}
        </div>
      </div>

      {/* Payment Summary */}
      <div className="wizard-card overflow-hidden">
        <div className="p-5 bg-[#0A1E40] text-white">
          <h3 className="font-semibold mb-4">Payment Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Visa Fee ({selectedVisa?.name})</span>
              <span>₹{baseFee.toLocaleString()}</span>
            </div>
            {tripDetails.num_travelers > 1 && (
              <div className="flex justify-between text-xs text-white/40 -mt-1">
                <span>₹{(selectedVisa?.fee || 0).toLocaleString()} × {tripDetails.num_travelers} travelers</span>
              </div>
            )}
            {expressProcessing && (
              <div className="flex justify-between text-sm">
                <span className="text-white/70 flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-[#FF5A36]" /> Express Processing</span>
                <span>₹{expressFee.toLocaleString()}</span>
              </div>
            )}
            {appointmentFee > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-white/70 flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5 text-[#FF5A36]" /> Appointment</span>
                <span>₹{appointmentFee.toLocaleString()}</span>
              </div>
            )}
            {partnerDiscount > 0 && (
              <>
                <div className="border-t border-white/10 pt-2"></div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-400 font-bold flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Partner Discount ({partnerDiscount}%)</span>
                  <span className="text-green-400 font-bold">- ₹{discountAmount.toLocaleString()}</span>
                </div>
              </>
            )}
            {partnerCommission > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-green-400 font-bold flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Commission ({partnerCommission}%)</span>
                <span className="text-green-400 font-bold">- ₹{commissionAmount.toLocaleString()}</span>
              </div>
            )}
            <div className="border-t border-white/20 pt-3 flex justify-between items-center">
              <span className="font-semibold">You Pay</span>
              <span className="text-2xl font-bold">₹{totalFee.toLocaleString()}</span>
            </div>
            {(partnerDiscount > 0 || partnerCommission > 0) && (
              <div className="text-xs text-green-400 font-bold text-right">
                You save ₹{(discountAmount + commissionAmount).toLocaleString()}
              </div>
            )}
          </div>
        </div>
        <div className="p-5 bg-[#0A1E40]/95 border-t border-white/10">
          <div className="bg-blue-500/20 rounded-xl p-4 mb-4">
            <p className="text-white/90 text-sm font-medium mb-1">Test Card Details (Demo Mode)</p>
            <p className="text-white/70 text-xs">Card: 4111 1111 1111 1111 | Expiry: Any future | CVV: Any 3 digits</p>
          </div>
          <Button
            onClick={handlePayment} disabled={loading}
            className="w-full bg-[#FF5A36] hover:bg-[#E0492B] text-white py-6 text-lg font-semibold rounded-xl"
            data-testid="pay-now-btn"
          >
            {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>) : (<>Pay ₹{totalFee.toLocaleString()} <ArrowRight className="w-5 h-5 ml-2" /></>)}
          </Button>
          <p className="text-white/40 text-xs text-center mt-3">Secured by RazorPay. 256-bit SSL encryption.</p>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return null;
    }
  };

  // =================== MAIN LAYOUT ===================
  if (editLoading) {
    return (
      <div className="min-h-screen bg-[#F5F0E6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5A36] mx-auto mb-3" />
          <p className="text-[#4A5568]">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <Navbar />
      <main className="pt-24 pb-28 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Left: Vertical Stepper Sidebar */}
            <div className="lg:col-span-2">
              <div className="sticky top-24">
                <div className="bg-white rounded-2xl border border-[#D1CBBF] p-4 hidden lg:block">
                  <div className="space-y-1">
                    {steps.map((step, index) => (
                      <React.Fragment key={step.number}>
                        <button
                          onClick={() => step.number < currentStep && setCurrentStep(step.number)}
                          className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-left ${
                            currentStep === step.number
                              ? 'bg-[#0A1E40] text-white shadow-md'
                              : currentStep > step.number
                                ? 'bg-green-50 text-green-700 cursor-pointer hover:bg-green-100'
                                : 'text-[#4A5568] hover:bg-[#F5F0E6]'
                          }`}
                          data-testid={`step-nav-${step.number}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            currentStep === step.number ? 'bg-[#FF5A36] text-white'
                              : currentStep > step.number ? 'bg-green-500 text-white' : 'bg-[#F5F0E6] text-[#4A5568] border border-[#D1CBBF]'
                          }`}>
                            {currentStep > step.number ? <CheckCircle className="w-4 h-4" /> : step.number}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold leading-tight truncate">{step.title}</p>
                            <p className={`text-[10px] leading-tight truncate ${
                              currentStep === step.number ? 'text-white/60' : 'text-[#9CA3AF]'
                            }`}>{step.subtitle}</p>
                          </div>
                        </button>
                        {index < steps.length - 1 && (
                          <div className="flex justify-center py-0.5">
                            <div className={`w-0.5 h-4 rounded-full ${currentStep > step.number ? 'bg-green-400' : 'bg-[#D1CBBF]'}`} />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Mobile horizontal stepper */}
                <div className="flex items-center gap-2 overflow-x-auto pb-2 hide-scrollbar lg:hidden">
                  {steps.map((step) => (
                    <button
                      key={step.number}
                      onClick={() => step.number < currentStep && setCurrentStep(step.number)}
                      className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        currentStep === step.number
                          ? 'bg-[#0A1E40] text-white'
                          : currentStep > step.number
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-white text-[#4A5568] border border-[#D1CBBF]'
                      }`}
                      data-testid={`step-nav-mobile-${step.number}`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        currentStep === step.number ? 'bg-[#FF5A36] text-white'
                          : currentStep > step.number ? 'bg-green-500 text-white' : 'bg-[#F5F0E6]'
                      }`}>
                        {currentStep > step.number ? <CheckCircle className="w-3 h-3" /> : step.number}
                      </div>
                      {step.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Center: Form Content */}
            <div className="lg:col-span-6">
              {/* Auto-save indicator */}
              {currentStep < 5 && lastSavedAt && (
                <div className="flex items-center justify-end gap-1.5 mb-3 text-xs text-[#4A5568]" data-testid="last-saved-indicator">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  <span>Auto-saved <span className="font-medium text-[#0A1E40]">{formatLastSaved()}</span></span>
                </div>
              )}
              {/* Step Content */}
              <div>{renderStepContent()}</div>

              {/* Navigation Buttons */}
              {currentStep < 5 && (
                <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                  <Button
                    variant="outline" onClick={() => setCurrentStep(currentStep - 1)}
                    disabled={currentStep === 1} className="border-[#D1CBBF] px-6"
                    data-testid="prev-step-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                  </Button>
                  <div className="flex items-center gap-2 ml-auto">
                    <Button
                      variant="outline"
                      onClick={handleSaveDraft}
                      disabled={savingDraft || loading}
                      className="border-[#0A1E40]/20 text-[#0A1E40] hover:bg-[#F5F0E6] px-5"
                      data-testid="save-draft-btn"
                    >
                      {savingDraft ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save as Draft
                    </Button>
                    <Button
                      onClick={handleNext} disabled={loading}
                      className="bg-[#FF5A36] hover:bg-[#E0492B] px-8"
                      data-testid="next-step-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Order Summary Sidebar (Always Visible) */}
            <div className="lg:col-span-4">
              <div className="sticky top-24 space-y-4">
                {/* Order Summary Card */}
                <div className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden" data-testid="order-summary">
                  <div className="p-5 bg-[#0A1E40] text-white">
                    <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif',color: '#FFFFFF' }} >Order Summary</h3>
                  </div>

                  <div className="p-5 space-y-4">
                    {selectedCountry ? (
                      <div className="flex items-center gap-3">
                        <CountryFlag code={selectedCountry.code} size="xl" />
                        <div>
                          <p className="font-semibold text-[#0A1E40]">{selectedCountry.name}</p>
                          <p className="text-xs text-[#4A5568]">{selectedVisa?.name || 'Select a plan'}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-[#4A5568]">Select a destination to begin</p>
                    )}

                    {selectedVisa && (
                      <>
                        <div className="border-t border-[#F5F0E6] pt-3 space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4A5568]">Visa Type</span>
                            <span className="font-medium text-[#0A1E40]">e-Visa</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4A5568]">Validity</span>
                            <span className="font-medium text-[#0A1E40]">{selectedVisa.validity}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4A5568]">Processing</span>
                            <span className="font-medium text-[#0A1E40]">{selectedVisa.processing_time}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4A5568]">Travelers</span>
                            <span className="font-medium text-[#0A1E40]">{tripDetails.num_travelers}</span>
                          </div>
                        </div>

                        <div className="border-t border-[#F5F0E6] pt-3 space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4A5568]">Visa Fee</span>
                            <span>₹{baseFee.toLocaleString()}</span>
                          </div>
                          {expressProcessing && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-[#4A5568] flex items-center gap-1"><Zap className="w-3 h-3 text-[#FF5A36]" /> Express</span>
                              <span>₹{expressFee.toLocaleString()}</span>
                            </div>
                          )}
                          {appointmentFee > 0 && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-[#4A5568] flex items-center gap-1"><CalendarIcon className="w-3 h-3 text-[#FF5A36]" /> Appointment</span>
                              <span>₹{appointmentFee.toLocaleString()}</span>
                            </div>
                          )}
                          {partnerDiscount > 0 && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-green-600 font-bold flex items-center gap-1"><Shield className="w-3 h-3" /> Discount ({partnerDiscount}%)</span>
                              <span className="text-green-600 font-bold">- ₹{discountAmount.toLocaleString()}</span>
                            </div>
                          )}
                          {partnerCommission > 0 && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-green-600 font-bold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Commission ({partnerCommission}%)</span>
                              <span className="text-green-600 font-bold">- ₹{commissionAmount.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between font-bold text-lg border-t border-[#F5F0E6] pt-2">
                            <span className="text-[#0A1E40]">You Pay</span>
                            <span className="text-[#FF5A36]">₹{totalFee.toLocaleString()}</span>
                          </div>
                          {(partnerDiscount > 0 || partnerCommission > 0) && (
                            <p className="text-xs text-green-600 font-bold text-right">You save ₹{(discountAmount + commissionAmount).toLocaleString()}</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="bg-white rounded-2xl border border-[#D1CBBF] p-5">
                  <div className="space-y-3">
                    {[
                      { icon: Shield, text: '100% Secure Payment' },
                      { icon: CheckCircle, text: '99.3% Approval Rate' },
                      { icon: Clock, text: 'On-Time Guaranteed' }
                    ].map((badge, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-[#4A5568]">
                        <badge.icon className="w-4 h-4 text-green-600" />
                        <span>{badge.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Sticky Price Footer */}
      {selectedVisa && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#FF5A36] shadow-[0_-4px_20px_rgba(0,0,0,0.15)]" data-testid="sticky-price-footer">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <p className="text-white/80 text-xs hidden sm:block">Total Price</p>
                <p className="text-white font-bold text-2xl sm:text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ₹{totalFee.toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-xs border-l border-white/30 pl-3">
                <span className="inline-flex items-center gap-1"><CountryFlag code={selectedCountry?.code} size="xs" /> {selectedCountry?.name}</span>
                <span className="hidden sm:inline">·</span>
                <span className="hidden sm:inline">{selectedVisa?.name}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-white text-sm">
              <span className="sm:hidden text-white/80 text-xs">{selectedVisa?.name}</span>
              <span className="font-medium">{tripDetails.num_travelers} Traveler{tripDetails.num_travelers > 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationWizard;
